﻿import { useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";

export interface LocationSearchParams {
  street_number?: string;
  street_name?: string;
  city?: string;
  state?: string;
  country?: string;
  place_id?: string;
  lat?: string;
  lng?: string;
}

export interface SetLocationOptions {
  /** Use history replace instead of push (e.g. while dragging the map, to avoid stacking back-history entries). */
  replace?: boolean;
}

export const useLocationSearchParams: () => [LocationSearchParams | undefined, (newValue: LocationSearchParams | undefined, options?: SetLocationOptions) => void] = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Create a stable string representation of searchParams for useMemo dependency
  const searchParamsString = searchParams.toString();

  // Derive location directly from searchParams
  const location = useMemo(() => {
    const currentStreetNumber = searchParams.get('street_number');
    const currentStreetName = searchParams.get('street_name');
    const currentCity = searchParams.get('city');
    const currentState = searchParams.get('state');
    const currentCountry = searchParams.get('country');
    const currentPlaceId = searchParams.get('place_id');
    const currentLat = searchParams.get('lat');
    const currentLng = searchParams.get('lng') || searchParams.get('long');

    // Helper to attach lat/lng when present
    const withLatLng = (params: LocationSearchParams): LocationSearchParams => {
      if (currentLat) params.lat = currentLat;
      if (currentLng) params.lng = currentLng;
      return params;
    };

    if (currentStreetNumber && currentStreetNumber.trim() !== "" &&
        currentStreetName && currentStreetName.trim() !== "" &&
        currentCity && currentState && currentCountry) {
      return withLatLng({
        street_number: currentStreetNumber,
        street_name: currentStreetName,
        city: currentCity,
        state: currentState,
        country: currentCountry,
        place_id: currentPlaceId || ""
      });
    } else if (currentCity && currentState && currentCountry) {
      return withLatLng({
        street_number: '',
        street_name: '',
        city: currentCity,
        state: currentState,
        country: currentCountry,
        place_id: currentPlaceId || ""
      });
    } else if (currentCity && currentState) {
      return withLatLng({
        city: currentCity,
        state: currentState,
        place_id: currentPlaceId || ""
      });
    } else if (currentLat && currentLng) {
      // Support lat/lng-only URLs (no city/state) — the cascade will reverse-geocode
      return {
        lat: currentLat,
        lng: currentLng,
        place_id: currentPlaceId || ""
      };
    }

    return undefined;
    // searchParams is a stable reference, so we need to use the string representation to detect changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, searchParamsString]);

  // Use a ref to hold the latest setSearchParams so the callback identity stays stable
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // Setter function that updates URL params (memoized for stable reference)
  const setLocation = useCallback((newLocation: LocationSearchParams | undefined, options?: SetLocationOptions) => {
    const updateOrDeleteSearchParam = (
      paramName: keyof LocationSearchParams,
      searchParams: URLSearchParams,
      location?: LocationSearchParams
    ) => {
      if (location?.[paramName]) {
        searchParams.set(paramName, location[paramName]);
      } else if (searchParams.has(paramName)) {
        searchParams.delete(paramName);
      }
    };

    setSearchParamsRef.current(prevSearchParams => {
      // Read current values from prevSearchParams (always up-to-date)
      const currentStreetNumber = prevSearchParams.get('street_number');
      const currentStreetName = prevSearchParams.get('street_name');
      const currentCity = prevSearchParams.get('city');
      const currentState = prevSearchParams.get('state');
      const currentCountry = prevSearchParams.get('country');
      const currentPlaceId = prevSearchParams.get('place_id');
      const currentLat = prevSearchParams.get('lat');
      const currentLng = prevSearchParams.get('lng') || prevSearchParams.get('long');

      // Check if any updates are needed
      const needsUpdate =
        newLocation?.street_number !== currentStreetNumber ||
        newLocation?.street_name !== currentStreetName ||
        newLocation?.city !== currentCity ||
        newLocation?.state !== currentState ||
        newLocation?.country !== currentCountry ||
        newLocation?.place_id !== currentPlaceId ||
        newLocation?.lat !== currentLat ||
        newLocation?.lng !== currentLng;

      if (needsUpdate) {
        updateOrDeleteSearchParam('street_number', prevSearchParams, newLocation);
        updateOrDeleteSearchParam('street_name', prevSearchParams, newLocation);
        updateOrDeleteSearchParam('city', prevSearchParams, newLocation);
        updateOrDeleteSearchParam('state', prevSearchParams, newLocation);
        updateOrDeleteSearchParam('country', prevSearchParams, newLocation);
        updateOrDeleteSearchParam('place_id', prevSearchParams, newLocation);
        updateOrDeleteSearchParam('lat', prevSearchParams, newLocation);
        updateOrDeleteSearchParam('lng', prevSearchParams, newLocation);
        // Normalize `long` → `lng`: remove the alias so the URL always uses `lng`
        if (prevSearchParams.has('long')) {
          prevSearchParams.delete('long');
        }
        // TODO (future ticket): remove state_region_dropdown if it's not needed
      }

      return prevSearchParams;
    }, options?.replace ? { replace: true } : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [location, setLocation];
}
